import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import {
  IHttpError,
  IIndicativeQuote,
  IndicativeQuoteRequest,
  Optimex,
  SendTradeParams,
  SuggestSlippageRequest,
  SuggestSlippageResponse,
  UpdateTradeParams,
} from '@/data'
import { IS_DISABLE_TURBO_SWAP } from '@/utils'

import { omitBy } from 'lodash-es'

export function useIndicativeQuote(params: IndicativeQuoteRequest, refetchInterval?: number, enabled = true) {
  const {
    fromTokenAmount,
    fromTokenId,
    toTokenId,
    sessionId,
    userRefundPubkey,
    fromUserAddress,
    userReceivingAddress,
  } = params
  return useQuery<IIndicativeQuote, IHttpError>({
    queryKey: [
      TANSTACK_QUERY_KEY.TRADE.GET_INDICATIVE_QUOTE_KEY,
      fromTokenAmount,
      fromTokenId,
      toTokenId,
      sessionId,
      userRefundPubkey,
      fromUserAddress,
      userReceivingAddress,
    ],
    queryFn: async () => {
      const requestPayload = omitBy(params, (value) => value === '') as IndicativeQuoteRequest
      const response = await Optimex.getIndicativeQuote(requestPayload)
      return response.data
    },
    enabled:
      !!fromTokenId && !!toTokenId && !!fromTokenAmount && fromTokenAmount !== '0' && !IS_DISABLE_TURBO_SWAP && enabled,
    refetchInterval: refetchInterval,
  })
}

const SEND_TRADE_INFO_KEY = 'SEND_TRADE_INFO_KEY'

export function useSendTradeInfo() {
  return useMutation<void, IHttpError, SendTradeParams>({
    mutationKey: [SEND_TRADE_INFO_KEY],
    mutationFn: async (payload) => {
      await Optimex.sendTradeInfo(payload)
    },
  })
}

export function useUpdateTradeInfo() {
  return useMutation<void, IHttpError, UpdateTradeParams>({
    mutationKey: ['update-trade-info'],
    mutationFn: async (payload) => {
      await Optimex.updateTradeInfo(payload)
    },
  })
}

export function useGetSuggestedSlippage(params: SuggestSlippageRequest) {
  const { fromTokenAmount, fromTokenId, toTokenId, type } = params
  return useQuery<SuggestSlippageResponse, IHttpError>({
    queryKey: ['get-suggested-slippage', fromTokenAmount, fromTokenId, toTokenId, type],
    queryFn: async () => {
      return await Optimex.getSuggestSlippage(params)
    },
    enabled: !!fromTokenId && !!fromTokenAmount,
    gcTime: 60_000,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}
